/**
 * In-memory mission service — demo data fixture.
 *
 * Status: pending replacement by the SL missions endpoint. Per Commander
 * posture "FE only consumes" (2026-04-25), this whole file should be deleted
 * once `/v1/missions/*` ships and the four consumer hooks
 * (`useScoutWeek`, `useStrategicRegions`, `useFixedAppointments`,
 * `useMorningSync`) migrate. The mock data lives in `missionService.mocks.ts`;
 * the type contracts live in `missionService.types.ts` and outlast this file.
 */

import type {
  CreateMissionFromRegionInput,
  CreateMissionInput,
  MissionGenerationParams,
  MissionRecord,
  MissionStoreRecord,
  WeeklyMission,
  WeeklyPlanningData,
} from './missionService.types'
import {
  holidaysByBundesland,
  initialMissionStore,
  strategicRegions,
  weeklyAppointments,
} from './missionService.mocks'

const NETWORK_DELAY = 450

const delay = (ms = NETWORK_DELAY) => new Promise((resolve) => setTimeout(resolve, ms))

const pickTimeSlot = (() => {
  const slots = ['09:00', '10:30', '13:00', '15:00', '16:30']
  let idx = 0
  return () => {
    const slot = slots[idx % slots.length]
    idx += 1
    return slot
  }
})()

let missionStore: MissionStoreRecord[] = [...initialMissionStore]

export function cloneMission(mission: MissionStoreRecord): MissionRecord {
  return {
    id: mission.id,
    title: mission.title,
    description: mission.description,
    status: mission.status,
    leadCount: mission.leadCount,
    region: mission.region,
    assignee: mission.assignee,
    startDate: mission.startDate ? new Date(mission.startDate) : undefined,
    endDate: mission.endDate ? new Date(mission.endDate) : undefined,
  }
}

function missionById(missionId: string): MissionStoreRecord {
  const mission = missionStore.find((m) => m.id === missionId)
  if (!mission) {
    throw new Error('Mission nicht gefunden')
  }
  return mission
}

/** Test-only: reset the in-memory store back to the seed fixtures. */
export function __resetMissionStoreForTests(): void {
  missionStore = [...initialMissionStore]
}

export const missionService = {
  async listMissions(): Promise<MissionRecord[]> {
    await delay()
    return missionStore.map((m) => cloneMission(m))
  },

  async createMission(input: CreateMissionInput): Promise<MissionRecord> {
    await delay()
    const record: MissionStoreRecord = {
      id: `mission-${Date.now()}`,
      title: input.title,
      description: input.description,
      status: input.status,
      leadCount: 0,
      region: input.region,
      assignee: input.assignee,
      startDate: input.startDate?.toISOString(),
      endDate: input.endDate?.toISOString(),
    }
    missionStore = [record, ...missionStore]
    return cloneMission(record)
  },

  async assignLeads(missionId: string, leadIds: string[]): Promise<MissionRecord> {
    await delay()
    if (!leadIds.length) {
      throw new Error('Keine Leads ausgewählt')
    }
    const mission = missionById(missionId)
    mission.leadCount += leadIds.length
    return cloneMission(mission)
  },

  async generateMissions(params: MissionGenerationParams): Promise<MissionRecord[]> {
    await delay(1200)
    const missionCount = Math.max(
      1,
      Math.min(3, params.leadCriteria ? 2 : 1 + Math.floor(Math.random() * 2)),
    )
    const regionLabel = params.region || 'Unbekannte Region'
    const generated: MissionStoreRecord[] = Array.from({ length: missionCount }).map((_, idx) => ({
      id: `mission-ai-${Date.now()}-${idx}`,
      title: `${regionLabel} AI Mission ${idx + 1}`,
      description: params.leadCriteria
        ? `Kriterien: ${params.leadCriteria}`
        : 'Automatisch generierte Mission',
      status: 'draft',
      leadCount: 0,
      region: regionLabel,
      assignee: undefined,
      startDate: undefined,
      endDate: undefined,
    }))
    missionStore = [...generated, ...missionStore]
    return generated.map(cloneMission)
  },

  async fetchWeeklyPlanningData(bundesland: string): Promise<WeeklyPlanningData> {
    await delay()
    const missions: WeeklyMission[] = missionStore
      .filter((mission) => Boolean(mission.scheduledDate))
      .map((mission) => ({
        id: mission.id,
        title: mission.title,
        region: mission.region ?? 'Unbekannt',
        time: mission.scheduledTime,
        leadCount: mission.leadCount,
        date: mission.scheduledDate!,
      }))

    const holidays = holidaysByBundesland[bundesland] ?? holidaysByBundesland.default
    return {
      missions,
      appointments: weeklyAppointments.map((entry) => ({ ...entry })),
      holidays: holidays.map((entry) => ({ ...entry })),
      regions: strategicRegions.map((region) => ({ ...region })),
    }
  },

  async createMissionFromRegion(input: CreateMissionFromRegionInput): Promise<WeeklyMission> {
    await delay()
    const region = strategicRegions.find((r) => r.id === input.regionId)
    if (region) {
      region.isAssigned = true
    }

    const newMission: MissionStoreRecord = {
      id: `mission-region-${Date.now()}`,
      title: `${input.regionName} Field Mission`,
      status: 'draft',
      leadCount: region?.leadCount ?? 0,
      region: input.regionName,
      assignee: undefined,
      startDate: input.scheduledDate,
      endDate: input.scheduledDate,
      scheduledDate: input.scheduledDate,
      scheduledTime: pickTimeSlot(),
    }

    missionStore = [newMission, ...missionStore]

    return {
      id: newMission.id,
      title: newMission.title,
      region: newMission.region ?? input.regionName,
      time: newMission.scheduledTime,
      leadCount: newMission.leadCount,
      date: newMission.scheduledDate!,
    }
  },
}
